import { getAuth } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import addLogDocument from "../addLogDocument.js";
import axios from "axios";
import JSZip from "jszip"; // Ensure the zip library is imported
import { getOrRefreshFirebaseToken } from "../../config/getFirebaseToken.js";

const generateBoth = async (
  xmlUrl,
  oid,
  listlots,
  xmlId,
  email,
  address,
  name,
  phone,
  prefix,
  orgData,
) => {
  const token = await getOrRefreshFirebaseToken();
  // Download the ZIP file
  const fileResponse = await axios.get(xmlUrl, { responseType: "arraybuffer" });
  try {
    if (fileResponse.status !== 200) {
      throw new Error(`Failed to download file: ${fileResponse.status}`);
    }

    if (
      // Check if the file is a ZIP
      !fileResponse.headers.get("content-type")?.includes("application/zip")
    ) {
      throw new Error(
        "the provided file is a " + fileResponse.headers.get("content-type")
      );
    }

    const zip = await JSZip.loadAsync(fileResponse.data);
    const xmlContent = await processZipBytes(zip);
    if (!xmlContent) throw new Error("No XML file found in the ZIP archive");

    let start = new Date();

    // Prepare logo
    const logo =
      orgData.config?.ath?.hide_my_logo === true
        ? "https://i.ibb.co/6NW6dkp/Hexagone-UXLogo3-LOW-1024.png"
        : orgData.logo || "https://i.ibb.co/8Nc2tS4/Placeholder.png";

    // --- Generate Insulator PDF ---
    const insulatorBody = {
      oid: oid,
      footer: `${name} - ${address}\nTéléphone : ${phone} - Mail : ${email}`,
      type: "insulator",
      batch: listlots,
      logo: logo,
      add_attestation: false
    };
    const insulatorFormData = new FormData();
    insulatorFormData.append(
      "file",
      new Blob([xmlContent]),
      "extracted.xml"
    );
    insulatorFormData.append("request", JSON.stringify(insulatorBody));
    const insulatorHeaders = {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`,
    };
    const insulatorUrl = prefix + `xml/${xmlId}/generate_pdf`;
    const insulatorResponse = await axios.post(insulatorUrl, insulatorFormData, {
      headers: insulatorHeaders,
    });
    if (insulatorResponse.status !== 200) {
      console.error("Failed to create insulator PDF:", insulatorResponse.status);
      return { url: "", attestationId: "" };
    }
    const urlinsulator = insulatorResponse.data.url.trim().replace(/"/g, "");

    // --- Generate Carbon PDF ---
    const carbonBody = {
      oid: oid,
      footer: `${name} - ${address}\nTéléphone : ${phone} - Mail : ${email}`,
      type: "carbon",
      batch: listlots,
      logo: logo,
      add_attestation: false
    };
    const carbonFormData = new FormData();
    carbonFormData.append(
      "file",
      new Blob([xmlContent]),
      "extracted.xml"
    );
    carbonFormData.append("request", JSON.stringify(carbonBody));
    const carbonHeaders = {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`,
    };
    const carbonUrl = prefix + `xml/${xmlId}/generate_pdf`;
    const carbonResponse = await axios.post(carbonUrl, carbonFormData, {
      headers: carbonHeaders,
    });
    if (carbonResponse.status !== 200) {
      console.error("Failed to create carbon PDF:", carbonResponse.status);
      return { url: "", attestationId: "" };
    }
    const urlcarbon = carbonResponse.data.url.trim().replace(/"/g, "");

    // --- Merge PDFs ---
    const mergeUrl = prefix + `xml/${xmlId}/merge`;
    const mergeBody = {
      pdf1: urlinsulator,
      pdf2: urlcarbon,
    };
    const mergeHeaders = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    const mergeResponse = await fetch(mergeUrl, {
      method: "POST",
      headers: mergeHeaders,
      body: JSON.stringify(mergeBody),
    });

    const currentUser = getAuth().currentUser;
    if (!currentUser) {
      return { url: "", attestationId: "" };
    }

    const userRef = doc(getFirestore(), "user", currentUser.uid);
    const userSnapshot = await getDoc(userRef);
    const orgId = userSnapshot.get("org_id");

    const responseText = await mergeResponse.text();
    let end = new Date();
    let elapsed = end - start;
    // Log the action
    await addLogDocument(
      mergeUrl,
      prefix,
      userRef,
      orgId,
      mergeResponse.status.toString(),
      "",
      "",
      JSON.stringify(mergeBody),
      responseText,
      "POST",
      elapsed
    );

    if (mergeResponse.ok) {
      const responseBody = JSON.parse(responseText);
      let url = responseBody.url.trim().replace(/"/g, "");
      let attestationId = responseBody.attestation_id;
      return { url: url, attestationId: attestationId };
    } else {
      console.error("Failed to merge PDFs:", mergeResponse.status);
      return { url: "", attestationId: "" };
    }
  } catch (error) {
    console.error("Exception occurred:", error);
    return { url: "", attestationId: "" };
  }
};

export const processZipBytes = async (zip) => {
  for (const filename in zip.files) {
    if (filename.endsWith(".xml") && !filename.includes("/")) {
      return await zip.file(filename).async("string");
    }
  }
  return null;
};

export default generateBoth;
